import { faCloudUpload } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useCallback, useEffect, useState } from 'react'
import { Accept, useDropzone } from 'react-dropzone'
import { FormTexts } from '../../../language/Texts'
import { ComponentType } from '../../../models/componentType.enum'
import './DropzoneComp.scss'
import Upload from '../../../assets/Upload.svg'


interface IFileLoaded {
    onFileLoaded: (file: File) => void
    type?: ComponentType;
}

export const acceptedDropzoneFileTypes: Accept = {
    'application/pdf': ['.pdf'],
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/png': ['.png'],
    'image/gif': ['.gif'],
    'image/webp': ['.webp'],
    'image/bmp': ['.bmp']
}

function startsWithSignature(fileBytes: Uint8Array, signature: number[]) {
    if (fileBytes.length < signature.length) {
        return false
    }

    return signature.every((byte, index) => fileBytes[index] === byte)
}

const fileSignatureMatchers: Record<string, (fileBytes: Uint8Array) => boolean> = {
    'application/pdf': (fileBytes) => startsWithSignature(fileBytes, [0x25, 0x50, 0x44, 0x46, 0x2D]),
    'image/jpeg': (fileBytes) => startsWithSignature(fileBytes, [0xFF, 0xD8, 0xFF]),
    'image/png': (fileBytes) => startsWithSignature(fileBytes, [0x89, 0x50, 0x4E, 0x47, 0x0D, 0x0A, 0x1A, 0x0A]),
    'image/gif': (fileBytes) =>
        startsWithSignature(fileBytes, [0x47, 0x49, 0x46, 0x38, 0x37, 0x61]) ||
        startsWithSignature(fileBytes, [0x47, 0x49, 0x46, 0x38, 0x39, 0x61]),
    'image/webp': (fileBytes) =>
        startsWithSignature(fileBytes, [0x52, 0x49, 0x46, 0x46]) &&
        startsWithSignature(fileBytes.slice(8), [0x57, 0x45, 0x42, 0x50]),
    'image/bmp': (fileBytes) => startsWithSignature(fileBytes, [0x42, 0x4D])
}

export function isSafeDropzoneFile(file: File) {
    const extensionIndex = file.name.lastIndexOf('.')
    if (extensionIndex < 0) {
        return false
    }

    const extension = file.name.slice(extensionIndex).toLowerCase()
    const allowedExtensions = acceptedDropzoneFileTypes[file.type]

    return Array.isArray(allowedExtensions) && allowedExtensions.includes(extension)
}

export function hasSafeDropzoneFileSignature(file: File, fileBytes: Uint8Array) {
    const signatureMatcher = fileSignatureMatchers[file.type]

    return typeof signatureMatcher === 'function' && signatureMatcher(fileBytes)
}

export default function DropzoneComp({ onFileLoaded, type }: IFileLoaded) {
    const [invalidFileWarning, setInvalidFileWarning] = useState(false)

    useEffect(() => {
        if (!invalidFileWarning) return
        const timer = setTimeout(() => setInvalidFileWarning(false), 3000)
        return () => clearTimeout(timer)
    }, [invalidFileWarning])

    const onDrop = useCallback((acceptedFiles: File[]) => {
        const safeFiles = acceptedFiles.filter(isSafeDropzoneFile)

        if (safeFiles.length < acceptedFiles.length) {
            setInvalidFileWarning(true)
        }

        safeFiles.forEach((file: File) => {
            const reader = new FileReader()
            reader.onabort = () => console.log('file reading was aborted')
            reader.onerror = () => console.log('file reading has failed')
            reader.onload = () => {
                const result = reader.result

                if (!(result instanceof ArrayBuffer)) {
                    return
                }

                if (!hasSafeDropzoneFileSignature(file, new Uint8Array(result))) {
                    setInvalidFileWarning(true)
                    return
                }

                setInvalidFileWarning(false)
                onFileLoaded(file)
            }
            reader.readAsArrayBuffer(file)
        })
    }, [onFileLoaded])
    const { getRootProps, getInputProps } = useDropzone({
        accept: acceptedDropzoneFileTypes,
        onDrop
    })

    return (
        <div {...getRootProps()}
            className={`dropzone_wrapper ${type === ComponentType.GEMUE_EXTERNAL ? 'gemue_dropzone_wrapper' : ''}`}
        >
            <input {...getInputProps()} data-testid='dropzone_input' style={{ display: "none" }} />
            {type !== ComponentType.GEMUE_EXTERNAL ? <FontAwesomeIcon
                className='upload_icon'
                icon={faCloudUpload} />
                :
                <button type='button' className='select_file_button input_field'>
                    <span className='button_content'>
                        <img src={Upload} alt="Upload" className='upload_icon  gemue_upload_icon' />
                        {FormTexts.chooseMultipleFile}
                    </span>
                </button>}
            {type !== ComponentType.GEMUE_EXTERNAL && <p className='drop_text'>{FormTexts.dragDropText}</p>}
            {invalidFileWarning && <p className='dropzone_invalid_warning'>{FormTexts.invalidFileType}</p>}
        </div>
    )
}